<script setup lang="ts">
import { toRef } from 'vue';

import GAnalytics from '@/analytics/ganalytics/utils/GAnalytics';
import SvgIcon from '@/common/components/SvgIcon.vue';
import { useCircleTypeInfo } from '@/elements/texts/curved/composables/useCircleTypeInfo';
import { Text } from '@/elements/texts/text/classes/Text';
import { useTextStyles } from '@/elements/texts/text/composables/useTextStyles';
import { useI18n } from '@/i18n/useI18n';

// Props
const props = defineProps<{ element: Text }>();

// Using composables
const element = toRef(props, 'element');

// Methods
const { updateTextAlign } = useTextStyles(element);
const { isCircleText } = useCircleTypeInfo(element);
const { trans } = useI18n();

const alignClickManager = () => {
	switch (props.element.textAlign) {
		case 'left': {
			updateTextAlign('center');
			break;
		}
		case 'center': {
			updateTextAlign('right');
			break;
		}
		case 'right': {
			updateTextAlign('justify');
			break;
		}
		case 'justify': {
			updateTextAlign('left');
			break;
		}
	}
	GAnalytics.track('click', 'Button', `text-align-${props.element.textAlign}`, null);
};
</script>

<template>
	<div class="flex flex-col">
		<button
			:disabled="isCircleText"
			:class="isCircleText ? 'cursor-not-allowed opacity-25' : 'hover:bg-gray-600'"
			class="flex h-9 w-full items-center justify-center rounded bg-gray-700 px-3 text-gray-100 mockup:bg-fp-gray-150 mockup:text-fp-gray-700 mockup:hover:text-blue-500"
			data-title="alignText"
			@click="alignClickManager()"
		>
			<SvgIcon
				v-if="element.textAlign === 'left'"
				name="alignleft"
				class="h-5 w-5"
				:class="{ 'fill-white mockup:fill-fp-gray-800': element.textAlign === 'left' }"
			/>
			<SvgIcon
				v-if="element.textAlign === 'center'"
				name="aligncenter"
				class="h-5 w-5"
				:class="{ 'fill-white mockup:fill-fp-gray-800': element.textAlign === 'center' }"
			/>
			<SvgIcon
				v-if="element.textAlign === 'right'"
				name="alignright"
				class="h-5 w-5"
				:class="{ 'fill-white mockup:fill-fp-gray-800': element.textAlign === 'right' }"
			/>
			<SvgIcon
				v-if="element.textAlign === 'justify'"
				name="alignjustify"
				class="h-5 w-5"
				:class="{ 'fill-white mockup:fill-fp-gray-800': element.textAlign === 'justify' }"
			/>
		</button>
		<label
			class="mt-1 text-center text-2xs text-gray-100 mockup:text-fp-gray-700"
			:class="isCircleText ? 'opacity-50' : ''"
		>
			{{ trans('Alignment') }}
		</label>
	</div>
</template>
